import React from 'react';
import { usePagination } from 'react-instantsearch';

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@repo/ui-components/shadcn/Pagination';
import breakpoints from '@repo/ui-components/breakpoints';

import 'instantsearch.css/themes/satellite.css';

const prepareUrl = url => url.replace(window.location.origin, '');

function CustomPagination(props) {
  const {
    currentRefinement,
    nbPages,
    isFirstPage,
    isLastPage,
    refine,
    createURL,
  } = usePagination(props);

  if (typeof window === 'undefined') {
    return null;
  }

  const isLargerViewport = window.innerWidth >= parseInt(breakpoints.lg, 10);

  // Calculate the visible pages based on the padding
  const padding = isLargerViewport ? 4 : 1; // Number of pages to show before and after the current page

  const firstPageIndex = 0;
  const lastPageIndex = nbPages - 1;
  const startPage = Math.max(currentRefinement - padding, firstPageIndex);
  const endPage = Math.min(currentRefinement + padding, lastPageIndex);

  const visiblePages = Array.from(
    { length: endPage - startPage + 1 },
    (_, i) => startPage + i
  );

  // Helper to determine if ellipses are needed
  const needsEllipsisBefore = startPage > firstPageIndex + 1;
  const needsEllipsisAfter = endPage < lastPageIndex - 1;

  return (
    <Pagination>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            to={prepareUrl(createURL(currentRefinement - 1))}
            onClick={e => {
              e.preventDefault();

              if (!isFirstPage) {
                refine(currentRefinement - 1);
              }
            }}
            disabled={isFirstPage}
          />
        </PaginationItem>

        {/* First Page and Ellipsis Before */}
        {startPage > firstPageIndex && (
          <>
            <PaginationItem>
              <PaginationLink
                to={prepareUrl(createURL(firstPageIndex))}
                onClick={e => {
                  e.preventDefault();
                  refine(firstPageIndex);
                }}
                aria-label={`Page ${firstPageIndex + 1}`}
              >
                {firstPageIndex + 1}
              </PaginationLink>
            </PaginationItem>
            {needsEllipsisBefore && (
              <PaginationItem>
                <PaginationEllipsis />
              </PaginationItem>
            )}
          </>
        )}

        {visiblePages.map(page => (
          <PaginationItem key={`pagination-item-${page}`}>
            <PaginationLink
              to={prepareUrl(createURL(page))}
              onClick={e => {
                e.preventDefault();
                refine(page);
              }}
              isActive={currentRefinement === page}
              aria-label={`Page ${page + 1}`}
            >
              {page + 1}
            </PaginationLink>
          </PaginationItem>
        ))}

        {/* Ellipsis After and Last Page */}
        {endPage < lastPageIndex && (
          <>
            {needsEllipsisAfter && (
              <PaginationItem>
                <PaginationEllipsis />
              </PaginationItem>
            )}
            <PaginationItem>
              <PaginationLink
                to={prepareUrl(createURL(lastPageIndex))}
                onClick={e => {
                  e.preventDefault();
                  refine(lastPageIndex);
                }}
                aria-label={`Page ${lastPageIndex + 1}`}
              >
                {lastPageIndex + 1}
              </PaginationLink>
            </PaginationItem>
          </>
        )}

        <PaginationItem>
          <PaginationNext
            to={prepareUrl(createURL(currentRefinement + 1))}
            onClick={e => {
              e.preventDefault();

              if (!isLastPage) {
                refine(currentRefinement + 1);
              }
            }}
            disabled={isLastPage}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

export default CustomPagination;
